import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiBaseUrls, ApiControllers } from '../constants/constants';
import { AddressVerificationModel, VerifyAddressRequest } from '../models/data/address.model';
import { ServiceResultModel } from '../models/service/service-result.model';
import { APISanitizerUtilities } from '../utilities/api-sanitizer.utilities';
import { ServiceUtilities } from '../utilities/service.utilities';

@Injectable({
  providedIn: 'root'
})
export class AddressService {
  private apiPath: string = `${ApiBaseUrls.CORE()}${ApiControllers.ADDRESSES()}`;

  constructor(private httpClient: HttpClient) { }

  /**
   * Verifies one address against the verification service. The only call that reaches the provider,
   * and a paid one for each address not seen before — call it on submit, never on blur or keystroke.
   * The API times the provider out at 10 seconds, so callers need a disabled, spinning submit.
   *
   * Callers must branch on statusCode: 503 and 403 mean verification is unavailable and the address
   * can still be saved unverified, while 422 means the provider rejected the input and the user has
   * to edit it.
   */
  public async verifyAddress(request: VerifyAddressRequest): Promise<ServiceResultModel<AddressVerificationModel>> {
    try {
      const requestUrl = `${this.apiPath}/verify`;

      const response = await firstValueFrom(
        this.httpClient.post<AddressVerificationModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<VerifyAddressRequest>(request)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      return <ServiceResultModel<AddressVerificationModel>>{
        data: <AddressVerificationModel>response.body?.valueOf(),
        success: true,
        statusCode: response.status
      };
    } catch (error) {
      // The status code decides which recovery the user is offered, and a body-less 503 from a
      // gateway carries none in its ProblemDetails, so it is backfilled from the transport.
      const result = ServiceUtilities.handleServiceException(error);
      return { ...result, statusCode: result.statusCode ?? ServiceUtilities.resolveStatusCode(error) };
    }
  }
}
